import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { getLogoWhite } from "@/utils/assets";
import { content } from "@/i18n/content";
import { useLang } from "@/i18n/language";
import { Section } from "./Primitives";

export function Publications() {
  const logo = getLogoWhite();
  const { lang } = useLang();
  const t = content[lang].publications;
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    const step = card.offsetWidth + 24;
    setActive(Math.round(el.scrollLeft / step));
  }, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [onScroll]);

  const go = (dir: -1 | 1) => {
    const el = scroller.current;
    if (!el) return;
    const card = el.firstElementChild as HTMLElement | null;
    if (!card) return;
    el.scrollBy({ left: dir * (card.offsetWidth + 24), behavior: "smooth" });
  };

  return (
    <Section id="publications" tone="dark">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-6">
        <div className="min-w-0">
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-primary/70" aria-hidden="true" />
            <p className="eyebrow text-primary">{t.eyebrow}</p>
          </div>
          <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-foreground md:text-5xl">
            {t.titleTop} <em className="italic text-primary/90">{t.titleAccent}</em>
          </h2>
        </div>
        <div className="hidden shrink-0 gap-3 md:flex">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Anterior"
            className="grid size-11 place-items-center rounded-full border border-foreground/15 text-foreground/70 transition-colors hover:border-primary hover:text-primary"
          >
            <ChevronLeft className="size-5" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Siguiente"
            className="grid size-11 place-items-center rounded-full border border-foreground/15 text-foreground/70 transition-colors hover:border-primary hover:text-primary"
          >
            <ChevronRight className="size-5" />
          </button>
        </div>
      </div>

      <div
        ref={scroller}
        className="mt-12 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {t.items.map((item, i) => (
          <article
            key={item.title}
            className="group relative flex w-[85%] shrink-0 snap-start flex-col overflow-hidden rounded-3xl border border-foreground/10 bg-surface sm:w-[62%] lg:w-[38%]"
            style={{ boxShadow: "var(--shadow-lift)" }}
          >
            <div className="relative aspect-square overflow-hidden">
              <div
                className="absolute inset-0"
                style={{ background: "var(--gradient-hero)" }}
                aria-hidden="true"
              />
              <img
                src={logo.url}
                alt=""
                aria-hidden="true"
                loading="lazy"
                className="absolute -bottom-6 left-1/2 w-[70%] -translate-x-1/2 opacity-25 transition-transform duration-500 group-hover:scale-105"
              />
              <div className="relative flex h-full flex-col p-6 md:p-8">
                <div className="flex items-center justify-between gap-4">
                  <p className="eyebrow text-primary">{item.meta}</p>
                  <span className="font-serif text-xl italic text-foreground/45">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 font-serif text-xl leading-snug text-foreground md:text-2xl">
                  {item.title}
                </h3>
              </div>
            </div>
            <div className="flex flex-1 flex-col justify-between gap-4 border-t border-foreground/10 p-6 md:p-8">
              <p className="text-sm text-muted-foreground">{item.source}</p>
              {item.url && (
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="eyebrow text-primary transition-opacity hover:opacity-70"
                >
                  {t.view} →
                </a>
              )}
            </div>
          </article>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-center gap-2" aria-hidden="true">
        {t.items.map((item, i) => (
          <span
            key={item.title}
            className={`h-1.5 rounded-full transition-all ${
              i === active ? "w-6 bg-primary" : "w-1.5 bg-foreground/25"
            }`}
          />
        ))}
      </div>

      <div className="mt-10 text-center">
        <a
          href={t.allUrl}
          target="_blank"
          rel="noreferrer"
          className="eyebrow text-primary transition-opacity hover:opacity-70"
        >
          {t.all} →
        </a>
      </div>
    </Section>
  );
}
